export interface OpaqueData {
	dataDescriptor: string;
	dataValue: string;
}

export interface AuthorizeNetCardData {
	cardNumber: string;
	month: string;
	year: string;
	cardCode?: string;
	zip?: string;
	fullName?: string;
}

export interface AuthorizeNetBankData {
	accountNumber: string;
	routingNumber: string;
	nameOnAccount: string;
	accountType: 'checking' | 'savings';
	zip?: string;
}

export interface AuthorizeNetAuthData {
	clientKey: string;
	apiLoginID: string;
}

export interface AuthorizeNetDispatchData {
	authData: AuthorizeNetAuthData;
	cardData?: AuthorizeNetCardData;
	bankData?: AuthorizeNetBankData;
}

export interface AuthorizeNetMessage {
	code: string;
	text: string;
}

export interface AuthorizeNetMessages {
	resultCode: 'Ok' | 'Error';
	message: AuthorizeNetMessage[];
}

export interface AuthorizeNetDispatchResponse {
	messages: AuthorizeNetMessages;
	opaqueData?: OpaqueData;
}

export interface PaymentGatewayConfig {
	processorType:
		| 'authorize_net'
		| 'epaymints'
		| 'cybersource'
		| 'nmi'
		| 'adyen';
	environment: 'sandbox' | 'production';
	publicCredentials: {
		// Authorize.net credentials (clientKey is shared with Adyen)
		apiLoginID?: string;
		clientKey?: string;
		// CyberSource credentials
		merchantId?: string;
		// NMI credentials
		tokenizationKey?: string;
		// Adyen credentials
		merchantAccount?: string;
		liveUrlPrefix?: string;
	};
}

export interface CreatePaymentMethodRequest {
	clientId: number;

	// Tokenized data from payment processor (required for all payment methods)
	dataDescriptor: string;
	dataValue: string;

	// Payment type and subtype
	paymentType?: PaymentMethodTypeEnum;
	paymentSubtype?: PaymentMethodSubtypeEnum;

	// Billing information (not sensitive)
	paymentName?: string;
	paymentAddress1: string;
	paymentAddress2?: string;
	paymentCity: string;
	paymentState: string;
	paymentZip: string;
	paymentCountry?: string;
	paymentEmail?: string;
	paymentIsPrimary?: boolean;

	// Display data - only masked values, never full card/account numbers
	// Credit Card fields
	cardLastFour?: string; // Last 4 digits of card number for display
	expirationDate?: string; // Backend stores expiration date

	// eCheck fields
	routingNumber?: string; // Backend stores full routing number in paymentRoutingNumber
	accountLastFour?: string; // Last 4 digits of account number for display
	nameOnAccount?: string; // Backend stores in paymentName
	accountType?: 'checking' | 'savings'; // Backend may store in paymentSubtype or custom field
}

export interface PaymentMethodFormData {
	paymentType: PaymentMethodTypeEnum;
	// Credit card fields
	cardNumber: string;
	expirationDate: string;
	nameOnCard: string;
	securityCode: string;
	// eCheck fields
	routingNumber: string;
	accountNumber: string;
	nameOnAccount: string;
	accountType: 'checking' | 'savings';
	// Address fields
	addressLine1: string;
	addressLine2: string;
	zipCode: string;
	state: string;
	city: string;
	// Email (required for CyberSource)
	email: string;
}

export enum PaymentMethodTypeEnum {
	ACH = 'ACH',
	CREDIT_CARD = 'Credit Card',
	CREDIT_CARD_READER = 'Credit Card Reader',
	CASH = 'Cash',
	CHECK = 'Check',
	COMPANY_CREDIT = 'Company Credit',
	ACCOUNT_FUNDS = 'Account Funds',
}

export enum PaymentMethodSubtypeEnum {
	CC_AMEX = 'Amex',
	CC_DINERS_CLUB = 'Diners Club',
	CC_DISCOVER = 'Discover',
	CC_JCB = 'JCB',
	CC_MASTERCARD = 'MasterCard',
	CC_VISA = 'Visa',
	ACH_CHECKING = 'Checking',
	ACH_SAVINGS = 'Savings',
}

export enum ProcessingFeeType {
	PERCENTAGE = '%',
	FLAT_RATE = '$',
}

export class PaymentGatewayError extends Error {
	constructor(
		message: string,
		public code?: string,
		public originalError?: unknown,
	) {
		super(message);
		this.name = 'PaymentGatewayError';
	}
}
